import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Loader2, XCircle, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { staggerParent, staggerItem } from '../lib/motion';
import { useCart } from '../context/CartContext';
import { getPaymentStatus, getOrder } from '../lib/api';

const MAX_POLLS = 12;

const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const { clearCart } = useCart();
  const [state, setState] = useState('checking'); // checking | paid | pending | error
  const [order, setOrder] = useState(null);
  const cleared = useRef(false);
  const polls = useRef(0);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    if (!sessionId) { setState('error'); return; }
    let timer;
    const poll = async () => {
      try {
        const res = await getPaymentStatus(sessionId);
        if (res.payment_status === 'paid') {
          setState('paid');
          if (!cleared.current) { clearCart(); cleared.current = true; }
          if (res.order_id) getOrder(res.order_id).then(setOrder).catch(() => {});
          return;
        }
        if (res.payment_status === 'failed' || res.payment_status === 'expired') {
          setState('error');
          return;
        }
        polls.current += 1;
        if (polls.current >= MAX_POLLS) { setState('pending'); return; }
        timer = setTimeout(poll, 2000);
      } catch {
        polls.current += 1;
        if (polls.current >= MAX_POLLS) { setState('error'); return; }
        timer = setTimeout(poll, 2000);
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, clearCart]);

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-24 sm:py-32 text-center" data-testid="payment-success-page">
      {state === 'checking' && (
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="flex flex-col items-center gap-5">
          <Loader2 size={42} className="text-[#c6a15b] animate-spin" />
          <h1 className="font-display text-4xl text-[#1a1a1a]">Confirming your payment…</h1>
          <p className="font-body text-[#6b6459]">Hang tight, this only takes a moment.</p>
        </motion.div>
      )}

      {state === 'paid' && (
        <motion.div variants={staggerParent(0.14)} initial="hidden" animate="show" className="flex flex-col items-center gap-5" data-testid="payment-confirmed">
          <motion.div variants={staggerItem}><CheckCircle2 size={56} className="text-[#4a8c5b] pop-in" /></motion.div>
          <motion.p variants={staggerItem} className="overline text-[#a8823c]">Order Confirmed</motion.p>
          <motion.h1 variants={staggerItem} className="font-display text-5xl text-[#1a1a1a] underline-grow">Thank you, queen.</motion.h1>
          <motion.p variants={staggerItem} className="font-body text-[#4a463d] max-w-md">Your payment was successful and your order is being prepared. A confirmation email is on its way.</motion.p>
          {order && (
            <motion.div variants={staggerItem} className="w-full bg-[#0b0b0b] p-8 mt-4 text-left">
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-display text-2xl text-[#f7f3ec]">Order Summary</h3>
                <span className="font-body text-xs text-[#9c9587]">#{order.order_id?.slice(0, 8)}</span>
              </div>
              <div className="space-y-4">
                {order.items?.map((it, idx) => (
                  <div key={idx} className="flex gap-3 items-center">
                    {it.image && <img src={it.image} alt={it.name} className="w-12 h-14 object-cover" />}
                    <div className="flex-1">
                      <p className="font-body text-sm text-[#f7f3ec] leading-tight">{it.name}</p>
                      <p className="font-body text-xs text-[#9c9587]">
                        {[it.length, it.color].filter(Boolean).join(' · ')}{it.length || it.color ? ' · ' : ''}Qty {it.quantity}
                      </p>
                    </div>
                    <span className="font-body text-sm text-[#e7d3a1]">${((it.unit_amount || 0) * it.quantity / 100).toFixed(2)}</span>
                  </div>
                ))}
              </div>
              <div className="gold-line my-5" />
              <div className="flex justify-between font-display text-lg">
                <span className="text-[#f7f3ec]">Items Total</span>
                <span className="text-[#e7d3a1]">${((order.amount_subtotal || 0) / 100).toFixed(2)}</span>
              </div>
              {(order.amount_discount || 0) > 0 && (
                <div className="flex justify-between font-body text-sm mt-2 text-emerald-400" data-testid="success-savings">
                  <span>Sale &amp; promo savings</span>
                  <span>−${((order.amount_discount || 0) / 100).toFixed(2)}</span>
                </div>
              )}
              <p className="font-body text-xs text-[#7a7365] mt-2">Flat $9.99 shipping &amp; tax were calculated at checkout.</p>
            </motion.div>
          )}
          <motion.div variants={staggerItem}>
            <Link to="/shop" className="btn-gold inline-flex items-center gap-2 px-9 py-4 mt-4"><span className="flex items-center gap-2">Continue Shopping <ArrowRight size={16} /></span></Link>
          </motion.div>
        </motion.div>
      )}

      {state === 'pending' && (
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="flex flex-col items-center gap-5">
          <Loader2 size={42} className="text-[#c6a15b]" />
          <h1 className="font-display text-4xl text-[#1a1a1a]">Payment processing</h1>
          <p className="font-body text-[#6b6459] max-w-md">Your payment is still being confirmed. You'll receive an email once it completes — no need to pay again.</p>
          <Link to="/" className="btn-gold inline-block px-9 py-4 mt-2"><span>Back to Home</span></Link>
        </motion.div>
      )}

      {state === 'error' && (
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="flex flex-col items-center gap-5" data-testid="payment-error">
          <XCircle size={52} className="text-[#b4453a] pop-in" />
          <h1 className="font-display text-4xl text-[#1a1a1a]">Something went wrong</h1>
          <p className="font-body text-[#6b6459] max-w-md">We couldn't confirm this payment. If you were charged, please contact us and we'll sort it out right away.</p>
          <Link to="/cart" className="btn-gold inline-block px-9 py-4 mt-2"><span>Return to Bag</span></Link>
        </motion.div>
      )}
    </div>
  );
};

export default PaymentSuccess;
